import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, StatCard, Select } from "@repo/o37-ui";
import AdminActivityModal from '../components/AdminActivityModal';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type AdminLog = {
  id: string;
  user_id: string | null;
  user_email: string | null;
  user_name: string | null;
  ip_address: string | null;
  user_agent: string | null;
  country: string | null;
  city: string | null;
  region: string | null;
  device_info: any;
  location_info: any;
  action_type: string;
  action_category: string | null;
  metadata: any;
  path: string | null;
  target_id: string | null;
  target_type: string | null;
  session_id: string | null;
  created_at: string;
};

type AggregatedUser = {
  id: string;
  email: string | null;
  name: string | null;
  lastActive: string;
  firstSeen: string;
  actionCount: number;
  location: string;
  country?: string;
  totalTime: number;
  devices: DeviceInfo[];
  logs: AdminLog[];
};

type DeviceInfo = {
  platform?: string;
  browser?: string;
  screenSize?: string;
  timezone?: string;
  userAgent?: string;
};

type LogStats = {
  totalLogs: number;
  last24Hours: number;
  lastWeek: number;
  uniqueUsers: number;
  byCategory: Record<string, number>;
  byActionType: Record<string, number>;
  topUsers: { userId: string; email: string; count: number }[];
};

const getCountryFlag = (countryCodeOrName?: string | null) => {
  if (!countryCodeOrName) return '🌍';
  
  if (countryCodeOrName.length === 2 && /^[A-Z]{2}$/i.test(countryCodeOrName)) {
    return countryCodeOrName
      .toUpperCase()
      .replace(/./g, char => String.fromCodePoint(char.charCodeAt(0) + 127397));
  }

  const map: Record<string, string> = {
    'United States': '🇺🇸', 'USA': '🇺🇸', 'US': '🇺🇸',
    'United Kingdom': '🇬🇧', 'UK': '🇬🇧',
    'Germany': '🇩🇪', 'France': '🇫🇷', 'Canada': '🇨🇦',
    'Australia': '🇦🇺', 'India': '🇮🇳', 'Japan': '🇯🇵',
  };
  
  return map[countryCodeOrName] || '🌍';
};

const getActionIcon = (type: string) => {
  const icons: Record<string, string> = {
    'login': '🔐', 'logout': '🚪', 'login_failed': '❌',
    'view_page': '👁️', 'click_button': '👆', 'click_link': '🔗', 'time_on_page': '⏱️',
    'view_users': '👥', 'view_user': '👤', 'create_user': '➕👤', 'update_user': '✏️👤', 'delete_user': '🗑️👤', 'update_permissions': '🔑',
    'view_vdr': '📁', 'view_vdr_logs': '📊', 'create_vdr': '➕📁', 'update_vdr': '✏️📁', 'delete_vdr': '🗑️📁',
    'create_link': '🔗➕', 'update_link': '🔗✏️', 'delete_link': '🔗🗑️',
    'create_page': '📄➕', 'update_page': '📄✏️', 'delete_page': '📄🗑️',
    'update_settings': '⚙️', 'update_profile': '👤✏️', 'change_password': '🔒',
  };
  return icons[type] || '•';
};

const getActionLabel = (type: string) => {
  const labels: Record<string, string> = {
    'login': 'Logged in', 'logout': 'Logged out', 'login_failed': 'Failed login attempt',
    'view_page': 'Viewed page', 'click_button': 'Clicked button', 'click_link': 'Clicked link', 'time_on_page': 'Time on page',
    'view_users': 'Viewed users list', 'view_user': 'Viewed user', 'create_user': 'Created user', 'update_user': 'Updated user', 'delete_user': 'Deleted user', 'update_permissions': 'Updated permissions',
    'view_vdr': 'Viewed VDR room', 'view_vdr_logs': 'Viewed VDR logs', 'create_vdr': 'Created VDR room', 'update_vdr': 'Updated VDR room', 'delete_vdr': 'Deleted VDR room',
    'create_link': 'Created access link', 'update_link': 'Updated access link', 'delete_link': 'Deleted access link',
    'create_page': 'Created page', 'update_page': 'Updated page', 'delete_page': 'Deleted page',
    'update_settings': 'Updated settings', 'update_profile': 'Updated profile', 'change_password': 'Changed password',
  };
  return labels[type] || type.replace(/_/g, ' ');
};

const getCategoryColor = (category: string | null) => {
  const colors: Record<string, string> = {
    'auth': 'bg-green-100 text-green-700',
    'users': 'bg-blue-100 text-blue-700',
    'vdr': 'bg-purple-100 text-purple-700',
    'settings': 'bg-orange-100 text-orange-700',
    'navigation': 'bg-neutral-100 text-neutral-700',
  };
  return colors[category || 'other'] || 'bg-neutral-100 text-neutral-600';
};

export default function AdminLogs() {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [users, setUsers] = useState<AggregatedUser[]>([]);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [selectedUser, setSelectedUser] = useState<AggregatedUser | null>(null);
  const [activeTab, setActiveTab] = useState('recent');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'users' | 'timeline'>('users');
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 200;

  const userRole = localStorage.getItem('app_role') || 'user';
  const userId = localStorage.getItem('app_user_id') || '';

  useEffect(() => {
    loadLogs();
    loadStats();
  }, []);

  const loadLogs = async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const currentOffset = isLoadMore ? offset + LIMIT : 0;
      const response = await fetch(`${API_URL}/admin-logs?limit=${LIMIT}&offset=${currentOffset}`, {
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError("Access denied. Super Admin only.");
        } else {
          setError("Failed to load admin logs.");
        }
        return;
      }

      const data = await response.json() as AdminLog[];
      
      if (data.length < LIMIT) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      if (isLoadMore) {
        setOffset(currentOffset);
        const newLogs = [...logs, ...data];
        setLogs(newLogs);
        processLogs(newLogs);
      } else {
        setOffset(0);
        setLogs(data);
        processLogs(data);
      }
      
    } catch (e) {
      console.error(e);
      setError("Error connecting to server.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/admin-logs/stats`, {
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Failed to load stats:', e);
    }
  };

  const processLogs = (logs: AdminLog[]) => {
    const userMap = new Map<string, AggregatedUser>();

    logs.forEach(log => {
      const key = log.user_id || log.user_email || 'unknown';
      
      if (!userMap.has(key)) {
        userMap.set(key, {
          id: key,
          email: log.user_email,
          name: log.user_name,
          lastActive: log.created_at,
          firstSeen: log.created_at,
          actionCount: 0,
          location: 'Unknown',
          country: log.country || undefined,
          totalTime: 0,
          devices: [],
          logs: []
        });
      }

      const user = userMap.get(key)!;
      user.logs.push(log);
      user.actionCount++;

      // Update timestamps
      if (new Date(log.created_at) > new Date(user.lastActive)) {
        user.lastActive = log.created_at;
      }
      if (new Date(log.created_at) < new Date(user.firstSeen)) {
        user.firstSeen = log.created_at;
      }

      // Update name/email if found
      if (!user.name && log.user_name) user.name = log.user_name;
      if (!user.email && log.user_email) user.email = log.user_email;

      // Track time
      if (log.action_type === 'time_on_page') {
        const ms = Number(log.metadata?.duration_ms) || 0;
        user.totalTime += (ms / 1000);
      }

      // Update location
      if (user.location === 'Unknown' && (log.city || log.country)) {
        user.location = [log.city, log.country].filter(Boolean).join(', ');
      }
      if (!user.country && log.country) {
        user.country = log.country;
      }

      // Track devices
      if (log.device_info) {
        const di = log.device_info;
        const deviceKey = `${di.platform || 'Unknown'}-${di.screen_width || 0}x${di.screen_height || 0}`;
        const existingDevice = user.devices.find(d => 
          `${d.platform}-${d.screenSize}` === deviceKey
        );
        if (!existingDevice) {
          user.devices.push({
            platform: di.platform,
            browser: parseBrowser(di.user_agent || log.user_agent),
            screenSize: di.screen_width && di.screen_height ? `${di.screen_width}x${di.screen_height}` : undefined,
            timezone: di.timezone,
            userAgent: di.user_agent || log.user_agent || undefined
          });
        }
      }
    });

    // Sort logs for each user
    userMap.forEach(user => {
      user.logs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    });

    setUsers(Array.from(userMap.values()));
  };

  const parseBrowser = (ua?: string | null): string => {
    if (!ua) return 'Unknown';
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    return 'Other';
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const m = Math.floor(seconds / 60);
    return `${m}m ${Math.round(seconds % 60)}s`;
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: 'numeric', minute: '2-digit'
    });
  };

  const getFilteredUsers = () => {
    let filtered = [...users];

    switch (activeTab) {
      case 'most_active':
        return filtered.sort((a, b) => b.actionCount - a.actionCount);
      case 'recent':
      default:
        return filtered.sort((a, b) => new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime());
    }
  };

  const getFilteredLogs = () => {
    let filtered = [...logs];
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(l => l.action_category === categoryFilter);
    }

    return filtered;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-neutral-400">Loading admin logs...</CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-red-500">{error}</CardContent>
      </Card>
    );
  }

  return (
    <div className="min-h-full">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-2xl font-semibold mb-2">Admin Activity Logs</h1>
          <p className="text-neutral-500">Monitor all admin panel activities and user actions.</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={viewMode === 'users' ? 'primary' : 'outline'} 
            onClick={() => setViewMode('users')}
            text="By User"
          />
          <Button 
            variant={viewMode === 'timeline' ? 'primary' : 'outline'} 
            onClick={() => setViewMode('timeline')}
            text="Timeline"
          />
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard 
            label="Total Actions" 
            value={stats.totalLogs.toString()} 
            hint="All time" 
          />
          <StatCard 
            label="Last 24 Hours" 
            value={stats.last24Hours.toString()} 
            hint="Recent activity" 
          />
          <StatCard 
            label="This Week" 
            value={stats.lastWeek.toString()} 
            hint="7-day total" 
          />
          <StatCard 
            label="Active Users" 
            value={stats.uniqueUsers.toString()} 
            hint="With activity" 
          />
        </div>
      )}

      {viewMode === 'users' ? (
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div>
                <CardTitle>Admin Users Activity</CardTitle>
                <p className="text-sm text-neutral-500">
                  Showing {logs.length} loaded events (Total: {stats?.totalLogs || '...'})
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'recent', label: 'Recent Activity' },
                { id: 'most_active', label: 'Most Active' },
              ].map(tab => (
                <Button 
                  key={tab.id}
                  variant={activeTab === tab.id ? 'primary' : 'outline'}
                  onClick={() => setActiveTab(tab.id)}
                  className="text-xs h-8 px-3"
                  text={tab.label}
                />
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-neutral-50 text-neutral-500 font-medium border-b border-neutral-100">
                  <tr>
                    <th className="px-6 py-3 w-12"></th>
                    <th className="px-6 py-3">User</th>
                    <th className="px-6 py-3">Location</th>
                    <th className="px-6 py-3 text-center">Actions</th>
                    <th className="px-6 py-3 text-center">Time Spent</th>
                    <th className="px-6 py-3 text-right">Last Active</th>
                    <th className="px-6 py-3 w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {getFilteredUsers().length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-neutral-400">
                        No admin activity recorded yet.
                      </td>
                    </tr>
                  ) : (
                    getFilteredUsers().map((user) => (
                      <tr key={user.id} className="hover:bg-neutral-50/50 transition-colors">
                        <td className="pl-6 py-4 text-2xl">
                          {getCountryFlag(user.country)}
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-neutral-900">
                              {user.name || user.email || 'Unknown User'}
                            </div>
                            {user.name && user.email && (
                              <div className="text-xs text-neutral-400 mt-0.5">{user.email}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-neutral-600">
                          {user.location || "Unknown"}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800">
                            {user.actionCount}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center text-neutral-600">
                          {formatDuration(user.totalTime)}
                        </td>
                        <td className="px-6 py-4 text-right text-neutral-500">
                          {formatTimeAgo(user.lastActive)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button 
                            variant="outline" 
                            onClick={() => setSelectedUser(user)}
                            text="View"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {hasMore && (
                <div className="p-4 text-center border-t border-neutral-100">
                  <Button 
                    variant="outline" 
                    onClick={() => loadLogs(true)}
                    disabled={loadingMore}
                    text={loadingMore ? "Loading more..." : "Load More Users"}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div>
                <CardTitle>Activity Timeline</CardTitle>
                <p className="text-sm text-neutral-500">
                  Showing {getFilteredLogs().length} of {stats?.totalLogs || '...'} events
                </p>
              </div>
              <div className="w-48">
                <Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                  <option value="all">All Categories</option>
                  <option value="auth">Authentication</option>
                  <option value="users">User Management</option>
                  <option value="vdr">VDR</option>
                  <option value="settings">Settings</option>
                  <option value="navigation">Navigation</option>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {getFilteredLogs().length === 0 ? (
                <div className="text-center text-neutral-400 py-8">
                  No activity recorded yet.
                </div>
              ) : (
                <>
                  {getFilteredLogs().map((log) => (
                    <div 
                      key={log.id}
                      className="flex items-start gap-4 p-4 rounded-lg hover:bg-neutral-50 transition-colors border border-neutral-100"
                    >
                      <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center text-lg shrink-0">
                        {getActionIcon(log.action_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-neutral-900 text-sm">
                              {log.user_name || log.user_email || 'Unknown User'}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${getCategoryColor(log.action_category)}`}>
                              {log.action_category || 'other'}
                            </span>
                          </div>
                          <span className="text-xs text-neutral-400 shrink-0">
                            {formatTimeAgo(log.created_at)} • {formatTime(log.created_at)}
                          </span>
                        </div>
                        <div className="text-sm text-neutral-600 mt-1">
                          {getActionLabel(log.action_type)}
                          {log.path && <span className="text-neutral-400"> • {log.path}</span>}
                        </div>
                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <div className="text-xs text-neutral-400 mt-1 truncate">
                            {log.metadata.label && `"${log.metadata.label}"`}
                            {log.metadata.target_email && ` → ${log.metadata.target_email}`}
                          </div>
                        )}
                      </div>
                      <div className="text-xl shrink-0">
                        {getCountryFlag(log.country)}
                      </div>
                    </div>
                  ))}
                  
                  {hasMore && (
                    <div className="pt-4 text-center">
                      <Button 
                        variant="outline" 
                        onClick={() => loadLogs(true)}
                        disabled={loadingMore}
                        text={loadingMore ? "Loading more..." : "Load More Activity"}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedUser && (
        <AdminActivityModal 
          user={selectedUser}
          logs={selectedUser.logs}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}
